import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface VideoStatusUpdate {
  type: 'videoStatusUpdate';
  videoId: string;
  status: {
    id: string;
    bunnyVideoId: string;
    status: string;
    isProcessing: boolean;
    isReady: boolean;
    hasError: boolean;
    statusMessage: string;
  };
  timestamp: string;
}

export interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds

  private connectionStatusSubject = new BehaviorSubject<'connecting' | 'connected' | 'disconnected'>('disconnected');
  private messagesSubject = new Subject<WebSocketMessage>();
  private videoStatusUpdatesSubject = new Subject<VideoStatusUpdate>();

  public connectionStatus$ = this.connectionStatusSubject.asObservable();
  public messages$ = this.messagesSubject.asObservable();
  public videoStatusUpdates$ = this.videoStatusUpdatesSubject.asObservable();

  private reconnectTimer: any = null;
  private pingInterval: any = null;

  constructor() {
    console.log('[WebSocketService] Service initialized');
  }

  /**
   * Connect to WebSocket server
   * @param userId Optional user ID to associate with this connection
   * @param orgId Optional organization ID
   */
  connect(userId?: string, orgId?: string): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      console.log('[WebSocketService] Already connected or connecting');
      return;
    }

    // Fail loudly on missing config rather than silently connecting somewhere.
    // This used to fall back to a hardcoded host, so blanking websocketUrl did
    // not disable sockets - it just moved them somewhere unintended.
    if (!environment.websocketUrl) {
      console.error(
        '[WebSocketService] environment.websocketUrl is not set - refusing to connect. ' +
        'Set it for this build configuration, or leave it unset deliberately to keep sockets off.'
      );
      this.connectionStatusSubject.next('disconnected');
      return;
    }

    // Get WebSocket URL from environment
    const wsUrl = this.getWebSocketUrl(userId, orgId);
    console.log(`[WebSocketService] Connecting to ${wsUrl}`);

    this.connectionStatusSubject.next('connecting');

    try {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = (event) => {
        console.log('[WebSocketService] Connected successfully', event);
        this.connectionStatusSubject.next('connected');
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.startPingInterval();
      };

      this.socket.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          console.log('[WebSocketService] Message received:', message);

          this.messagesSubject.next(message);

          // Handle specific message types
          if (message.type === 'videoStatusUpdate') {
            this.videoStatusUpdatesSubject.next(message as VideoStatusUpdate);
          }
        } catch (error) {
          console.error('[WebSocketService] Error parsing message:', error);
        }
      };

      this.socket.onerror = (error) => {
        console.error('[WebSocketService] Error:', error);
      };

      this.socket.onclose = (event) => {
        console.log('[WebSocketService] Connection closed:', event.code, event.reason);
        this.connectionStatusSubject.next('disconnected');
        this.stopPingInterval();

        // Attempt to reconnect if not intentionally closed
        if (event.code !== 1000) {
          this.scheduleReconnect(userId, orgId);
        }
      };

    } catch (error) {
      console.error('[WebSocketService] Failed to create WebSocket connection:', error);
      this.connectionStatusSubject.next('disconnected');
      this.scheduleReconnect(userId, orgId);
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    console.log('[WebSocketService] Disconnecting...');

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopPingInterval();

    if (this.socket) {
      this.socket.close(1000, 'Client disconnecting');
      this.socket = null;
    }

    this.connectionStatusSubject.next('disconnected');
  }

  /**
   * Send a message through the WebSocket
   */
  send(message: WebSocketMessage): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
      console.log('[WebSocketService] Message sent:', message);
    } else {
      console.warn('[WebSocketService] Cannot send message, not connected');
    }
  }

  /**
   * Get the current connection status
   */
  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  private scheduleReconnect(userId?: string, orgId?: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WebSocketService] Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);

    console.log(`[WebSocketService] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.connect(userId, orgId);
    }, delay);
  }

  /**
   * Start sending ping messages to keep connection alive
   */
  private startPingInterval(): void {
    this.stopPingInterval();

    // Send ping every 30 seconds
    this.pingInterval = setInterval(() => {
      if (this.isConnected()) {
        this.send({ type: 'ping', action: 'ping', timestamp: new Date().toISOString() });
      }
    }, 30000);
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Get WebSocket URL with optional user and org parameters
   */
  private getWebSocketUrl(userId?: string, orgId?: string): string {
    // Single source of truth. Do NOT reintroduce a hardcoded fallback host: a
    // second copy of the URL goes stale silently the day the socket moves, and
    // it masks missing config instead of surfacing it. connect() guards for
    // emptiness before calling this.
    const baseUrl = environment.websocketUrl;

    // Add query parameters if provided
    const params = new URLSearchParams();
    if (userId) {
      params.append('userId', userId);
    }
    if (orgId) {
      params.append('orgId', orgId);
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
